/**
 * `canary local login` — Re-login / refresh credentials for a local environment.
 *
 * @module
 */

import process from 'node:process';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import { getArgValue, resolveConfig } from '../auth.js';
import {
  selectProperty,
  promptChoice,
  fetchList,
  uploadStorageState,
  apiRequest,
} from '../cli-helpers.js';
import {
  createSession,
  deleteSession,
  getSessionStorageState,
} from '../session/daemon-client.js';
import type { CredentialListItem } from '../cli-helpers.js';

interface LocalEnvironment {
  id: string;
  name: string;
  url: string;
  propertyId: string;
}

export async function runLocalLogin(argv: string[]) {
  const config = await resolveConfig(argv);
  const propertyHint = getArgValue(argv, '--property');
  const envHint = getArgValue(argv, '--environment');
  const credentialHint = getArgValue(argv, '--credential');
  const modeFlag = getArgValue(argv, '--mode');

  if (modeFlag && modeFlag !== 'agent' && modeFlag !== 'manual') {
    console.error(`--mode must be one of: agent, manual (got "${modeFlag}")`);
    process.exitCode = 1;
    return;
  }

  // Select property
  const property = await selectProperty(config.apiUrl, config.token, propertyHint);
  if (!property) {
    process.exitCode = 1;
    return;
  }

  // Get local environments
  const envs = await fetchList<LocalEnvironment>(
    config.apiUrl,
    config.token,
    `/org/properties/${property.id}/environments/local`,
    'items'
  );

  if (envs.length === 0) {
    console.error('No local environments found. Run `canary local init` first.');
    process.exitCode = 1;
    return;
  }

  let selectedEnv: LocalEnvironment;
  if (envHint) {
    const match = envs.find(
      (e) => e.id === envHint || e.name.toLowerCase() === envHint.toLowerCase()
    );
    if (!match) {
      console.error(`Environment "${envHint}" not found.`);
      console.error('Available environments:');
      for (const e of envs) {
        console.error(`  - ${e.name} (${e.url})`);
      }
      process.exitCode = 1;
      return;
    }
    selectedEnv = match;
  } else if (envs.length === 1) {
    selectedEnv = envs[0];
  } else {
    const picked = await promptChoice(
      'Select a local environment:',
      envs.map((e) => ({ label: `${e.name} — ${e.url}`, value: e }))
    );
    if (!picked) {
      process.exitCode = 1;
      return;
    }
    selectedEnv = picked;
  }

  // Find credentials for this property
  const allCredentials = await fetchList<CredentialListItem>(
    config.apiUrl,
    config.token,
    '/org/credentials',
    'items'
  );

  const envCredentials = allCredentials.filter(
    (c) => c.propertyId === property.id
  );

  if (envCredentials.length === 0) {
    console.error('No credentials found for this property.');
    process.exitCode = 1;
    return;
  }

  let credential: CredentialListItem;
  if (credentialHint) {
    const match = envCredentials.find(
      (c) => c.id === credentialHint || c.name.toLowerCase() === credentialHint.toLowerCase()
    );
    if (!match) {
      console.error(`Credential "${credentialHint}" not found.`);
      console.error('Available credentials:');
      for (const c of envCredentials) {
        console.error(`  - ${c.name}`);
      }
      process.exitCode = 1;
      return;
    }
    credential = match;
  } else if (envCredentials.length === 1) {
    credential = envCredentials[0];
  } else {
    const picked = await promptChoice(
      'Select a credential to refresh:',
      envCredentials.map((c) => ({ label: c.name, value: c }))
    );
    if (!picked) {
      process.exitCode = 1;
      return;
    }
    credential = picked;
  }

  // If the credential has login instructions + stored creds, offer the no-browser path first.
  const detail = await fetchCredentialLoginInfo(
    config.apiUrl,
    config.token,
    property.id,
    credential.id
  );
  const canAgentLogin = Boolean(
    detail?.loginInstructions &&
      detail.data?.username &&
      detail.data?.password &&
      (detail.loginUrl || selectedEnv.url)
  );

  let mode: 'agent' | 'manual';
  if (modeFlag === 'agent' || modeFlag === 'manual') {
    if (modeFlag === 'agent' && !canAgentLogin) {
      console.error(
        'Credential is missing login instructions or stored username/password; cannot run agent login.'
      );
      process.exitCode = 1;
      return;
    }
    mode = modeFlag;
  } else if (canAgentLogin) {
    const choice = await promptChoice<'agent' | 'manual'>('How would you like to log in?', [
      { label: 'Let the agent log in (no browser, fastest) — recommended', value: 'agent' },
      { label: "Open a browser and I'll log in myself", value: 'manual' },
    ]);
    if (!choice) {
      process.exitCode = 1;
      return;
    }
    mode = choice;
  } else {
    if (!detail?.loginInstructions) {
      console.log(
        '\nTip: add login instructions to this credential so the agent can log in for you next time.'
      );
    }
    mode = 'manual';
  }

  if (mode === 'agent') {
    const ok = await runAgentLogin({
      apiUrl: config.apiUrl,
      token: config.token,
      propertyId: property.id,
      credentialId: credential.id,
      credentialName: credential.name,
    });
    if (!ok) {
      console.log('\nFalling back to manual browser login.');
      await runManualLogin({
        apiUrl: config.apiUrl,
        token: config.token,
        propertyId: property.id,
        credentialId: credential.id,
        url: selectedEnv.url,
      });
    }
    return;
  }

  await runManualLogin({
    apiUrl: config.apiUrl,
    token: config.token,
    propertyId: property.id,
    credentialId: credential.id,
    url: selectedEnv.url,
  });
}

type CredentialLoginInfo = {
  id: string;
  name: string;
  authProvider?: string;
  loginUrl?: string | null;
  loginInstructions?: string | null;
  data?: { username?: string; password?: string } | null;
};

async function fetchCredentialLoginInfo(
  apiUrl: string,
  token: string,
  propertyId: string,
  credentialId: string
): Promise<CredentialLoginInfo | null> {
  const result = await apiRequest<{
    ok: boolean;
    credential?: CredentialLoginInfo;
    error?: string;
  }>(apiUrl, token, 'GET', `/org/properties/${propertyId}/credentials/${credentialId}`);
  if (!result.ok || !result.credential) return null;
  return result.credential;
}

async function runAgentLogin(args: {
  apiUrl: string;
  token: string;
  propertyId: string;
  credentialId: string;
  credentialName: string;
}): Promise<boolean> {
  console.log(`\nAsking the agent to log in as "${args.credentialName}"...`);

  const res = await fetch(
    `${args.apiUrl}/org/properties/${args.propertyId}/credentials/${args.credentialId}/mcp-test/stream`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.token}`,
        Accept: 'text/event-stream',
      },
    }
  );

  if (!res.ok || !res.body) {
    console.error(`Agent login could not start (HTTP ${res.status}).`);
    return false;
  }

  let success: boolean | null = null;
  let errorMessage: string | null = null;

  const parser = createParser({
    onEvent: (event: EventSourceMessage) => {
      if (!event.data) return;
      let data: unknown;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      switch (event.event) {
        case 'status': {
          const { message } = data as { message?: string };
          if (message) console.log(`  ${message}`);
          break;
        }
        case 'log': {
          const { level, message } = data as { level?: string; message?: string };
          if (message && level !== 'debug') {
            console.log(`  ${message}`);
          }
          break;
        }
        case 'complete': {
          const summary = data as { ok?: boolean; message?: string | null };
          success = summary.ok === true;
          if (!success && summary.message) errorMessage = summary.message;
          break;
        }
        case 'error': {
          const { message } = data as { message?: string };
          errorMessage = message ?? 'Agent login failed';
          success = false;
          break;
        }
      }
    },
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    parser.feed(decoder.decode(value, { stream: true }));
    if (success !== null && !success && errorMessage) break;
  }

  if (success) {
    console.log('\nLogin state refreshed by agent. Run `canary local start` to begin.');
    return true;
  }

  console.error(`\nAgent login failed: ${errorMessage ?? 'unknown error'}`);
  return false;
}

async function runManualLogin(args: {
  apiUrl: string;
  token: string;
  propertyId: string;
  credentialId: string;
  url: string;
}): Promise<void> {
  console.log(`\nOpening browser at ${args.url}...`);
  const sessionResult = await createSession({
    name: 'local-login',
    url: args.url,
  });

  if (!sessionResult.ok || !sessionResult.data) {
    console.error(`Failed to start browser: ${sessionResult.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  const sessionId = sessionResult.data.id;

  console.log('Log in, then press Enter when done.');

  const readline = await import('node:readline');
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  await new Promise<void>((resolve) => {
    rl.question('', () => {
      rl.close();
      resolve();
    });
  });

  console.log('Extracting login state...');
  const storageResult = await getSessionStorageState(sessionId);

  if (!storageResult.ok || !storageResult.data) {
    console.error(`Failed to extract storage state: ${storageResult.error ?? 'Unknown error'}`);
    await deleteSession(sessionId);
    process.exitCode = 1;
    return;
  }

  console.log('Saving login state...');
  const uploadResult = await uploadStorageState({
    apiUrl: args.apiUrl,
    token: args.token,
    propertyId: args.propertyId,
    credentialId: args.credentialId,
    storageState: storageResult.data,
  });

  if (!uploadResult.ok) {
    console.error(`Failed to save login state: ${uploadResult.error ?? 'Unknown error'}`);
    await deleteSession(sessionId);
    process.exitCode = 1;
    return;
  }

  await deleteSession(sessionId);
  console.log('\nLogin state refreshed! Run `canary local start` to begin.');
}
